import React from 'react';

export const itemColors = [
  'rgba(96,165,250,0.35)',
  'rgba(74,222,128,0.35)',
  'rgba(250,204,21,0.35)',
  'rgba(248,113,113,0.35)',
  'rgba(167,139,250,0.35)',
  'rgba(244,114,182,0.35)',
  'rgba(129,140,248,0.35)',
  'rgba(52,211,153,0.35)'
];

export const getCellSize = (width, length) => {
  const largestSide = Math.max(width, length, 1);

  if (largestSide <= 8) return 34;
  if (largestSide <= 12) return 28;
  if (largestSide <= 18) return 22;
  return 18;
};

export const getCellKey = (row, column) => `${row}-${column}`;

const parseCellKey = (cellKey) => {
  const [row, column] = cellKey.split('-').map(Number);
  return { row, column };
};

const getGroupLookup = (groups = [], colorPalette = [], fallbackColor = 'rgba(255,255,255,0.08)') => {
  const lookup = {};

  groups.forEach((group, index) => {
    const color = group.color || colorPalette[index % colorPalette.length] || fallbackColor;

    (group.cells || []).forEach((cellKey) => {
      lookup[cellKey] = {
        label: group.label,
        color,
        status: group.status || '',
        notes: group.notes || ''
      };
    });
  });

  return lookup;
};

const getGroupBounds = (cells = []) => {
  if (!cells.length) return null;

  const positions = cells.map(parseCellKey);
  const rows = positions.map((position) => position.row);
  const columns = positions.map((position) => position.column);

  return {
    minRow: Math.min(...rows),
    maxRow: Math.max(...rows),
    minColumn: Math.min(...columns),
    maxColumn: Math.max(...columns)
  };
};

function RoomGrid({
  width,
  length,
  items = [],
  issueGroups = [],
  selectedCells = [],
  onCellClick,
  onCellMouseDown,
  onCellMouseEnter,
  onGridMouseUp,
  emptyColor = 'rgba(255,255,255,0.06)',
  caption,
  showCaption = true
}) {
  const totalCells = width * length;
  const cellSize = getCellSize(width, length);
  const cellGap = 4;
  const itemLookup = getGroupLookup(items, itemColors, emptyColor);
  const issueLookup = getGroupLookup(issueGroups, [], 'rgba(248,113,113,0.4)');
  const selectedLookup = selectedCells.reduce((accumulator, cellKey) => {
    accumulator[cellKey] = true;
    return accumulator;
  }, {});

  if (!width || !length) {
    return (
      <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
        Enter room dimensions to generate a preview.
      </div>
    );
  }

  return (
    <div>
      {showCaption && (
        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>
          {caption || `Preview: ${width} x ${length} squares`}
        </div>
      )}
      <div
        style={{
          position: 'relative',
          display: 'inline-block',
          overflowX: 'auto',
          paddingBottom: '6px'
        }}
        onMouseUp={onGridMouseUp}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${width}, ${cellSize}px)`,
            gap: `${cellGap}px`,
            justifyContent: 'start'
          }}
        >
          {Array.from({ length: totalCells }).map((_, index) => {
            const row = Math.floor(index / width) + 1;
            const column = (index % width) + 1;
            const cellKey = getCellKey(row, column);
            const issue = issueLookup[cellKey];
            const item = itemLookup[cellKey];
            const isSelected = Boolean(selectedLookup[cellKey]);
            const background = isSelected ? 'rgba(249,115,22,0.45)' : issue ? issue.color : item ? item.color : emptyColor;
            const title = issue
              ? `${issue.label}${issue.notes ? ` - ${issue.notes}` : ''}`
              : item
                ? item.label
                : `Row ${row}, Column ${column}`;

            return (
              <button
                key={cellKey}
                type="button"
                onClick={onCellClick ? () => onCellClick(cellKey) : undefined}
                onMouseDown={onCellMouseDown ? () => onCellMouseDown(cellKey) : undefined}
                onMouseEnter={onCellMouseEnter ? () => onCellMouseEnter(cellKey) : undefined}
                style={{
                  width: `${cellSize}px`,
                  height: `${cellSize}px`,
                  border: isSelected ? '2px solid #f97316' : issue ? '1px solid #f87171' : '1px solid rgba(255,255,255,0.12)',
                  background,
                  borderRadius: '4px',
                  padding: 0,
                  cursor: onCellClick || onCellMouseDown ? 'pointer' : 'default',
                  userSelect: 'none'
                }}
                title={title}
              />
            );
          })}
        </div>

        {items.map((item, index) => {
          const bounds = getGroupBounds(item.cells || []);

          if (!bounds) return null;

          const left = (bounds.minColumn - 1) * (cellSize + cellGap);
          const top = (bounds.minRow - 1) * (cellSize + cellGap);
          const boxWidth = ((bounds.maxColumn - bounds.minColumn) + 1) * cellSize + ((bounds.maxColumn - bounds.minColumn) * cellGap);
          const boxHeight = ((bounds.maxRow - bounds.minRow) + 1) * cellSize + ((bounds.maxRow - bounds.minRow) * cellGap);

          return (
            <div
              key={`item-${item.label}-${index}`}
              style={{
                position: 'absolute',
                left: `${left}px`,
                top: `${top}px`,
                width: `${boxWidth}px`,
                height: `${boxHeight}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                padding: '2px',
                overflow: 'visible',
                zIndex: 2
              }}
            >
              <div
                style={{
                  padding: '2px 6px',
                  background: 'rgba(15, 15, 18, 0.88)',
                  border: '1px solid rgba(255,255,255,0.16)',
                  borderRadius: '999px',
                  fontSize: cellSize < 22 ? '9px' : '11px',
                  fontWeight: 'bold',
                  color: 'var(--text-main)',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.35)'
                }}
                title={item.label}
              >
                {item.label}
              </div>
            </div>
          );
        })}

        {issueGroups.map((issue, index) => {
          const bounds = getGroupBounds(issue.cells || []);

          if (!bounds) return null;

          const left = (bounds.minColumn - 1) * (cellSize + cellGap);
          const top = (bounds.minRow - 1) * (cellSize + cellGap);
          const boxWidth = ((bounds.maxColumn - bounds.minColumn) + 1) * cellSize + ((bounds.maxColumn - bounds.minColumn) * cellGap);
          const boxHeight = ((bounds.maxRow - bounds.minRow) + 1) * cellSize + ((bounds.maxRow - bounds.minRow) * cellGap);

          return (
            <div
              key={`issue-${issue.id || issue.label}-${index}`}
              style={{
                position: 'absolute',
                left: `${left}px`,
                top: `${top}px`,
                width: `${boxWidth}px`,
                height: `${boxHeight}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                padding: '2px',
                overflow: 'visible',
                zIndex: 3
              }}
            >
              <div
                style={{
                  padding: '2px 6px',
                  background: issue.overlayBackground || 'rgba(179, 38, 30, 0.85)',
                  borderRadius: '999px',
                  border: `1px solid ${issue.borderColor || '#f87171'}`,
                  fontSize: cellSize < 22 ? '9px' : '11px',
                  fontWeight: 'bold',
                  color: '#fecaca',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.35)'
                }}
                title={issue.notes ? `${issue.label} - ${issue.notes}` : issue.label}
              >
                {issue.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RoomGrid;
